import * as React from 'react'
import { useNavigate } from 'react-router-dom';
import { Leaf, X } from 'lucide-react';

import LogoImg from './images/logo.jpg'
import QrImg from './images/qr_zoc.jpg'
import CertGxjsImg from './images/cert_gxjs.jpg'
import CertIso9001Img from './images/cert_iso9001.jpg'
import CertIso20000Img from './images/cert_iso20000.jpg'
import CertIso27001Img from './images/cert_iso27001.jpg'

import casesData from './cases.json';
import './styles/main.css'

// 七牛云图片OSS
const cdnPath = 'http://img.zocdesign.net/cases-dev/'


// 文件名模板：
// ux_0_seg13.jpg  |   ux_thumb_0.jpg  |  ux_thumb_hero_0.jpg
// mk_2_seg8.jpg  |  mk_thumb_2.jpg  |  mk_thumb_hero_2.jpg
// bak:{ "id": 9, "type": "ux", "name": "华福小福牛5.0", "brand": "华福证券", "segNum": 9 },


// 从json中读取案例信息
const cases_raw: CaseInput[] = casesData.cases_raw as CaseInput[];
const caseGridOrder = casesData.caseGridOrder;


// 根据案例信息，构建案例对象，用于heors和grids
let caseRenderObjs: CaseRenderObjs = { ux: [], mk: [] }
for (let c of cases_raw) {
  caseRenderObjs[c.type].push({
    id: c.id,
    type: c.type,
    name: c.name,
    brand: c.brand,
    thumb: cdnPath + `${c.type}_thumb_${c.id}.jpg`,
    main: cdnPath + `${c.type}_${c.id}_seg`,
    segNum: c.segNum,
    isHero: c.isHero,
    heroThumb: c.isHero ? cdnPath + `${c.type}_thumb_hero_${c.id}.jpg` : ''
  })
}

// 构建Heros
const heros = {
  ux: caseRenderObjs.ux.filter(c => c.isHero)[0],
  mk: caseRenderObjs.mk.filter(c => c.isHero)[0]
}




// 根据caseGridOrder来排序ux和mk类型的案例
const uxGrid = caseRenderObjs.ux.sort((a, b) => caseGridOrder.ux.indexOf(a.id) - caseGridOrder.ux.indexOf(b.id)).filter(obj => !obj.isHero);
const mkGrid = caseRenderObjs.mk.sort((a, b) => caseGridOrder.mk.indexOf(a.id) - caseGridOrder.mk.indexOf(b.id)).filter(obj => !obj.isHero);


type PageView = 'about' | 'esg' | 'ux' | 'marketing';

const listingPathForType = (type: CaseRenderObj['type']) => type === 'mk' ? '/marketing' : '/ux';

const EsgDivider = () => (
  <div className='esg-divider' aria-hidden='true'>
    <span className='esg-divider-line' />
    <Leaf className='esg-divider-icon' size={18} strokeWidth={1.5} />
    <span className='esg-divider-line' />
  </div>
);

type EsgCertificate = {
  title: string;
  label: string;
  src: string;
};

const esgCertificates: EsgCertificate[] = [
  { title: '高新技术企业证书', label: '高新技术企业证书', src: CertGxjsImg },
  { title: 'ISO 9001质量管理体系认证', label: 'ISO 9001质量管理体系认证', src: CertIso9001Img },
  { title: 'ISO/IEC 20000-1信息技术服务管理体系认证', label: 'ISO/IEC 20000-1信息技术服务管理体系认证', src: CertIso20000Img },
  { title: 'ISO/IEC 27001信息安全管理体系认证', label: 'ISO/IEC 27001信息安全管理体系认证', src: CertIso27001Img },
];

const App = ({ page }: { page: PageView }) => {
  const navigate = useNavigate();
  const contentRef = React.useRef<HTMLDivElement>(null);
  const closeCertificateButtonRef = React.useRef<HTMLButtonElement>(null);
  const [activeCertificate, setActiveCertificate] = React.useState<EsgCertificate | null>(null);
  const isIntroPage = page === 'about' || page === 'esg';

  React.useEffect(() => {
    contentRef.current?.scrollTo(0, 0);
  }, [page]);

  React.useEffect(() => {
    if (!activeCertificate || page !== 'esg') return;

    const contentElement = contentRef.current;
    const previousOverflowY = contentElement?.style.overflowY;
    if (contentElement) contentElement.style.overflowY = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActiveCertificate(null);
    };

    window.addEventListener('keydown', handleKeyDown);
    closeCertificateButtonRef.current?.focus();

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (contentElement && previousOverflowY !== undefined) {
        contentElement.style.overflowY = previousOverflowY;
      }
    };
  }, [activeCertificate, page]);

  React.useEffect(() => {
    setActiveCertificate(null);
  }, [page]);

  return (
    <div className='page'>
      {/* ====== SIDE:START ====== */}
      <div className='case-side'>
        <div className='case-title'>
          <img src={LogoImg} width={205} className='logo' alt='logo'></img>
        </div>
        <div className='case-tab'>
          <button type='button' className={isIntroPage ? 'btnActive' : 'none'} onClick={() => { navigate('/about') }}>卓奇简介</button>
          <button type='button' className={page === 'ux' ? 'btnActive' : 'none'} onClick={() => { navigate('/ux') }}>产品UX</button>
          <button type='button' className={page === 'marketing' ? 'btnActive' : 'none'} onClick={() => { navigate('/marketing') }}>专题营销</button>
        </div>
        <div className='side-qr'>
          <img src={QrImg} alt='ZOC.D公众号' width={150}></img>
          <p>扫码关注卓奇咨询官方微信公众号</p>
        </div>
        <div className='side-icp'>
          ICP证：<a href='https://beian.miit.gov.cn' target='_blank' rel='noreferrer'>苏ICP备13009085号-3</a>
        </div>
      </div>
      {/* ====== SIDE:END ====== */}

      {/* ====== CONTENT:START ====== */}
      <div ref={contentRef} className='case-list-assemble'>
        <div className='case-list-body'>
          {isIntroPage && <div className='info-tab'>
            <button type='button' className={page === 'about' ? 'btnActive' : 'none'} onClick={() => { navigate('/about') }}>关于卓奇</button>
            <button type='button' className={page === 'esg' ? 'btnActive' : 'none'} onClick={() => { navigate('/esg') }}>可持续发展</button>
          </div>}

          {page === 'about' && <div className='info-content' style={{ borderRadius: '12px', fontSize: 0, overflow: 'hidden' }}>
            <img src={`${cdnPath}intro1.jpg`} alt='' width='1000' />
            <img src={`${cdnPath}intro2.jpg`} alt='' width='1000' />
            <img src={`${cdnPath}intro3.jpg`} alt='' width='1000' />
            <img src={`${cdnPath}intro4.jpg`} alt='' width='1000' />
          </div>}

          {page === 'esg' && <article className='esg-content'>
            <header className='esg-header'>
              <p className='esg-company'>南京卓奇软件设计有限公司</p>
              <h1>ESG信息披露及实践概述</h1>
            </header>

            <div className='esg-intro'>
              <p className='esg-lead'>南京卓奇软件设计有限公司长期服务金融机构数字化产品建设与体验优化项目，重视在经营管理和项目交付中平衡专业服务、客户权益、信息安全、知识产权及员工发展等责任要求。</p>
              <p className='esg-note'>本概述为公司ESG相关信息披露与实践说明，不替代第三方ESG评级报告或完整ESG报告。</p>
            </div>

            <section className='esg-section'>
              <h2>一、环境责任：推进数字化协同与资源节约</h2>
              <p>公司以数字产品体验咨询、交互设计、视觉设计及数字化运营设计为主要服务内容，项目成果通常以电子文档、设计源文件、原型文件及线上协作方式完成交付。</p>
              <p>在符合客户项目管理及保密要求的前提下，公司持续推进数字化协作和电子化交付，提升跨地域团队及客户协同效率，减少非必要的重复制作与纸质材料使用。对于涉及线下物料的项目，公司在设计阶段结合实际应用尺寸、信息层级及使用场景进行规划，提升物料使用效率。</p>
            </section>

            <EsgDivider />

            <section className='esg-section'>
              <h2>二、社会责任：以专业服务改善金融数字化体验</h2>
              <p>公司持续服务证券及金融行业数字化产品，围绕用户研究、体验评测、产品策略、交互设计、视觉设计及设计规范建设等环节，帮助客户提升金融服务的清晰度、易用性和一致性。</p>
              <p>在用户研究项目中，公司建立样本筛选、访谈组织、数据整理及研究质量控制机制。对于参与深度访谈的用户，项目组通过意愿筛选后开展后续邀约与组织，并在项目实施中做好用户隐私保护及信息保密，避免将用户研究资料用于项目约定范围以外的用途。</p>
              <p>公司重视专业人才培养与客户能力共建。在项目服务过程中，可结合客户需求提供用户体验方法论、设计理念、设计规范及专项业务场景培训，推动项目经验和专业能力的有效沉淀与传播。</p>
            </section>

            <EsgDivider />

            <section className='esg-section'>
              <h2>三、公司治理：以质量、信息安全、保密与知识产权管理保障服务交付</h2>
              <p>公司实行总经理负责制，并设置交付、研究及运营支持等职能体系，对项目质量、资源协调、经营管理、客户沟通及专业研究进行协同管理。</p>
              <p>在项目质量管理方面，公司建立项目计划、阶段评审、交付审核及风险预警机制。项目成果提交前实行设计负责人审核与项目负责人复核的双重审核机制；通过定期进度沟通、问题反馈及风险预警，保障服务质量与交付进度。</p>
              <p>在信息安全、客户保密及知识产权管理方面，公司重视客户资料、项目文档、设计成果及第三方素材的合规使用。项目实施中，项目成员按照约定承担保密义务；项目资料、设计文档及相关成果仅用于约定项目范围。对于设计中使用的图片、字体、音效及其他素材，公司重视版权风险识别与使用管理，并配合客户完成相应的版权确认及交付要求。</p>
              <p>公司已建立与业务相适应的质量、信息技术服务及信息安全管理能力。投标文件可随附公司持有的相关有效认证材料，包括：</p>
              <ul className='esg-certificate-grid'>
                {esgCertificates.map((certificate) => (
                  <li key={certificate.title}>
                    <button
                      type='button'
                      className='esg-certificate-card'
                      aria-label={`查看${certificate.title}大图`}
                      onClick={() => setActiveCertificate(certificate)}
                    >
                      <span className='esg-certificate-thumb'>
                        <img src={certificate.src} alt={certificate.title} loading='lazy' />
                      </span>
                      <span className='esg-certificate-label'>{certificate.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>

            <EsgDivider />

            <section className='esg-section'>
              <h2>四、公开访问路径及核验方式</h2>
              <p>公司官方公开渠道：公司官方网站；</p>
              <p>认证证书核验：可通过认证证书二维码、认证机构公开渠道进行核验；</p>
              <p>知识产权公开查询：可通过国家知识产权局相关公开查询渠道，以“南京卓奇软件设计有限公司”为申请人进行检索。</p>
            </section>

            <div className='esg-intro esg-closing'>
              <p className='esg-lead'>公司将持续把责任经营、专业服务、客户信息保护、质量管理及人才发展融入日常经营和项目交付，在为客户创造价值的同时，推动与客户、员工、行业及社会的长期共赢。</p>
            </div>
            <p className='esg-updated'>更新日期：2026年7月3日</p>
          </article>}

          {page === 'esg' && activeCertificate && <div
            className='esg-certificate-lightbox'
            role='dialog'
            aria-modal='true'
            aria-label={`${activeCertificate.title}大图预览`}
            onClick={() => setActiveCertificate(null)}
          >
            <div className='esg-certificate-lightbox-panel' onClick={(event) => event.stopPropagation()}>
              <button
                ref={closeCertificateButtonRef}
                type='button'
                className='esg-certificate-lightbox-close'
                aria-label='关闭证书大图预览'
                onClick={() => setActiveCertificate(null)}
              >
                <X size={22} aria-hidden='true' />
              </button>
              <img
                className='esg-certificate-lightbox-image'
                src={activeCertificate.src}
                alt={activeCertificate.title}
              />
            </div>
          </div>}

          {page === 'ux' && <>
          <div className='case-hero'>
            {/* UX Hero */}
            <div className='link' onClick={() => navigate('/single/', { state: { caseObj: heros.ux, from: '/ux' } })} >
              <img src={heros.ux.heroThumb} alt='' width='1000' height='380' />
            </div>
          </div>
          <div className='case-grid'>
            {uxGrid.map(
              (c, id) => <CaseThumb caseObj={c} key={id} />
            )}
          </div>
          </>}

          {page === 'marketing' && <>
          <div className='case-hero'>
            <div className='link' onClick={() => navigate('/single/', { state: { caseObj: heros.mk, from: '/marketing' } })}>
              <img src={heros.mk.heroThumb} alt='' width='1000' height='380' />
            </div>
          </div>
          <div className='case-grid'>
            {mkGrid.map(
              (c, id) => <CaseThumb caseObj={c} key={id} />
            )}
          </div>
          </>}
        </div>
      </div>
      {/* ====== CONTENT:END ====== */}
    </div>
  )
}




const CaseThumb = ({ caseObj }: { caseObj: CaseRenderObj }) => {
  let navigate = useNavigate();
  return (
    <div className='case-block link' onClick={() => navigate('/single/', { state: { caseObj: caseObj, from: listingPathForType(caseObj.type) } })}>

      <div style={{ display: "grid" }}>
        <div className='case-block-img'>
          <img src={caseObj.thumb} alt={caseObj.name.toString()} width='315' height='210' />
        </div>
      </div>

    </div>
  )
}

export default App
